import { IMMUTABLE_GAME_ID, IMMUTABLE_GAME_NAME } from "@/constants";
import { usePassportProvider } from "@/context/PassportProvider";
import { globalTelegramQuest } from "@/data-access/quests/globalTelegramQuest";
import { submitQuestCompletion } from "@/hooks/quest/useSubmitQuest";
import { useAccessToken, useAccessTokenState } from "@/hooks/useAccessToken";
import { authorizedGet } from "@/utils/request";
import { trackEvent } from "@analytics";
import {
  Badge,
  Body,
  Box,
  ButtCon,
  Button,
  Heading,
  Icon,
  Stack,
} from "@biom3/react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import type { LinkedAccount } from "./ProfileDrawer/ConnectionsOverlay";
import { TelegramQuestModal } from "./QuestModals/TelegramQuestModal";

export function TelegramConnectQuestBanner() {
  const [isMounted, setIsMounted] = useState(false);
  const [isBannerClosed, setIsBannerClosed] = useState(false);
  const [showQuestModal, setShowQuestModal] = useState(false);
  const [isCompletingQuest, setIsCompletingQuest] = useState(false);
  const { walletAddress } = usePassportProvider();
  const accessToken = useAccessTokenState();
  const linkedAccountsKey = "/passport-profile/v1/linked-accounts";
  const gemsKey = `wallet/gems/${walletAddress}`;
  const pathName = usePathname();
  const {
    data = { linked_accounts: [] },
    isLoading: isLoadingLinkedAccounts,
    error: linkedAccountsError,
  } = useSWR<{ linked_accounts: LinkedAccount[] }>(
    accessToken ? linkedAccountsKey : null,
    (path: string) => authorizedGet(path, accessToken as string),
    { revalidateOnFocus: false },
  );
  const telegramAccount = useMemo(
    () =>
      data.linked_accounts.find((account) => account.platform === "telegram"),
    [data.linked_accounts],
  );
  const getAccessToken = useAccessToken();
  const { mutate } = useSWRConfig();
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleClose = useCallback(() => {
    trackEvent({
      component: "Button",
      action: "Pressed",
      extras: {
        control: "Close",
        section: "TelegramConnectQuestBanner",
      },
    });
    setIsBannerClosed(true);
    setIsCompletingQuest(false);
    if (walletAddress) {
      localStorage.setItem(
        `showTelegramConnectQuestBanner-${walletAddress}`,
        "false",
      );
    }
  }, [walletAddress]);

  const showBanner = useMemo(() => {
    if (isCompletingQuest) {
      return true;
    }
    if (!isMounted || !walletAddress) {
      return false;
    }
    if (
      localStorage.getItem(
        `showTelegramConnectQuestBanner-${walletAddress}`,
      ) === "false"
    ) {
      return false;
    }
    if (isBannerClosed) {
      return false;
    }
    if (isLoadingLinkedAccounts) {
      return false;
    }
    if (linkedAccountsError) {
      return false;
    }
    if (telegramAccount) {
      return false;
    }
    if (pathName?.includes("immutable-play-onboarding")) {
      return false;
    }
    return true;
  }, [
    isMounted,
    isLoadingLinkedAccounts,
    linkedAccountsError,
    telegramAccount,
    isBannerClosed,
    walletAddress,
    isCompletingQuest,
    pathName,
  ]);

  const handleOpenConnectModal = useCallback(() => {
    setIsCompletingQuest(true);
    trackEvent({
      component: "Button",
      action: "Pressed",
      extras: {
        control: "ConnectNow",
        section: "TelegramConnectQuestBanner",
      },
    });
    setShowQuestModal(true);
  }, []);

  const handleQuestComplete = useCallback(async () => {
    try {
      await submitQuestCompletion(globalTelegramQuest.eventId, getAccessToken);
      setShowQuestModal(false);
      mutate(gemsKey);
    } catch (error) {
      setShowQuestModal(false);
    }
  }, [getAccessToken, mutate, gemsKey]);

  if (!showBanner) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          pb: "base.spacing.x2",
          position: "relative",
          borderRadius: "base.spacing.x2",
          mb: "base.spacing.x4",
          overflow: "hidden",
          backgroundImage: `url("/images/telegram-connect-banner-background.png")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            p: "base.spacing.x8",
            zIndex: 1,
            position: "relative",
            paddingX: "base.spacing.x12",
          }}
        >
          <Stack
            direction={["column", "column", "row"]}
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            gap="base.spacing.x4"
          >
            <Stack
              sx={{
                minWidth: ["auto", "auto", "350px"],
                alignItems: ["center", "center", "flex-start"],
                textAlign: ["center", "center", "left"],
              }}
            >
              <Badge
                badgeContent="New"
                variant="emphasis"
                sx={{ mb: "base.spacing.x1" }}
              />
              <Heading
                size="medium"
                weight="bold"
                color="primary"
                sx={{
                  mb: "base.spacing.x1",
                }}
              >
                Connect with Telegram. Get exclusives.
              </Heading>
              <Body
                size="small"
                weight="regular"
                color="primary"
                sx={{ maxWidth: "345px" }}
              >
                Don&apos;t miss out on the best rewards, launches and more.
              </Body>
            </Stack>

            <Stack direction="row" alignItems="center" gap="base.spacing.x4">
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                gap="base.spacing.x2"
                sx={{
                  display: ["none", "none", "flex"],
                  mr: "base.spacing.x4",
                }}
              >
                <Image
                  src="/images/quests/immutable-logo.svg"
                  width="55"
                  height="55"
                  alt="Immutable Logo"
                />
                <Image
                  id="double-sided-arrow"
                  src="/images/quests/double-sided-arrow.svg"
                  alt="Link"
                  width={30}
                  height={10}
                />
                <Image
                  src="/images/quests/telegram-logo.svg"
                  width="55"
                  height="55"
                  alt="Telegram Logo"
                />
              </Stack>
              <Button
                variant="primary"
                size="large"
                onClick={handleOpenConnectModal}
              >
                <Stack
                  direction="column"
                  alignItems="center"
                  gap="base.spacing.x1"
                  color="primary"
                >
                  Connect now
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    gap="base.spacing.x1"
                  >
                    <Body
                      size="small"
                      weight="bold"
                      sx={{
                        color: "inherit",
                        textAlign: "center",
                      }}
                    >
                      +{globalTelegramQuest.increase}
                    </Body>
                    <Icon
                      icon="ImxRewards"
                      sx={{
                        textAlign: "center",
                        fill: "inherit",
                        w: "base.icon.size.200",
                      }}
                      variant="bold"
                    />
                  </Stack>
                </Stack>
              </Button>
            </Stack>
          </Stack>

          <ButtCon
            icon="Close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: [
                "base.spacing.x4",
                "base.spacing.x4",
                "base.spacing.x4",
                "base.spacing.x4",
                "base.spacing.x4",
                "base.spacing.x2",
              ],
              right: ["base.spacing.x2", "base.spacing.x4"],
              backgroundColor: "base.color.neutral.700",
              "&:hover": {
                backgroundColor: "base.color.neutral.800",
              },
            }}
            variant="tertiary"
            size="medium"
          />
        </Box>
      </Box>
      {showQuestModal && (
        <TelegramQuestModal
          gameId={IMMUTABLE_GAME_ID}
          gameName={IMMUTABLE_GAME_NAME}
          questKey={globalTelegramQuest.key}
          ruleId={globalTelegramQuest.ruleId}
          onClose={() => {
            setIsCompletingQuest(false);
            setShowQuestModal(false);
          }}
          onQuestComplete={handleQuestComplete}
        />
      )}
    </>
  );
}
